import * as React from "react";
import { isStringEmpty } from "../common/stringUtils";

const canUsePassiveEvents = (): boolean => {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return false;

  let passive = false;
  const options = Object.defineProperty({}, "passive", {
    // eslint-disable-next-line getter-return
    get() {
      passive = true;
    },
  });
  const noop = (): null => null;

  window.addEventListener("test", noop, options);
  window.removeEventListener("test", noop, options);

  return passive;
};

export const DEFAULT_IGNORE_CLASS = "ignore-onclickoutside";

export interface Callback<T extends Event = Event> {
  (event: T): void;
}
type El = HTMLElement;
type Refs = React.RefObject<El>[];
export interface Options {
  disabled?: boolean;
  eventTypes?: string[];
  excludeScrollbar?: boolean;
  ignoreClass?: string;
  detectIFrame?: boolean;
  rootId?: string;
}
interface Return {
  (element: El | null): void;
}

const hasIgnoreClass = (e: any, ignoreClass: string): boolean => {
  let el = e.target || e;

  while (el) {
    if (el.classList?.contains(ignoreClass)) return true;
    el = el.parentElement;
  }

  return false;
};

const getEventOptions = (type: string): { passive: boolean } | boolean =>
  type.includes("touch") && canUsePassiveEvents() ? { passive: true } : false;

const useOnclickOutside = (
  callback: Callback,
  {
    disabled,
    eventTypes = ["mousedown", "touchstart"],
    excludeScrollbar,
    ignoreClass = DEFAULT_IGNORE_CLASS,
    detectIFrame = true,
    rootId = "",
  }: Options = {}
): Return => {
  const callbackRef = React.useRef(callback);
  const [refsState, setRefsState] = React.useState<React.RefObject<HTMLElement>>();

  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const ref: Return = React.useCallback((el) => setRefsState({ current: el }), []);

  React.useEffect(
    () => {
      if (!refsState?.current) return;

      const getEls = (): HTMLElement | null => {
        return refsState.current;
      };

      const handler = (e: any): void => {
        const paths = e?.composedPath?.();
        const el = getEls();
        const myId = el?.id ?? null;

        if (!isStringEmpty(myId) && paths.find((element: HTMLElement) => element?.id === myId)) {
          return;
        }

        if (el && el.contains(e.target)) {
          return;
        }

        if (e.target.id && rootId && rootId === e.target.id) {
          return;
        }

        callbackRef.current(e);
      };

      const blurHandler = (e: FocusEvent) =>
        // On firefox the iframe becomes document.activeElement in the next event loop
        setTimeout(() => {
          const { activeElement } = document;

          if (
            activeElement?.tagName === "IFRAME" &&
            !hasIgnoreClass(activeElement, ignoreClass) &&
            getEls() !== activeElement
          )
            callbackRef.current(e);
        }, 0);

      const removeEventListener = (): void => {
        eventTypes.forEach((type) =>
          // @ts-expect-error
          document.removeEventListener(type, handler, getEventOptions(type))
        );

        if (detectIFrame) window.removeEventListener("blur", blurHandler);
      };

      if (disabled) {
        removeEventListener();
        return;
      }

      eventTypes.forEach((type) => document.addEventListener(type, handler, getEventOptions(type)));

      if (detectIFrame) window.addEventListener("blur", blurHandler);

      return (): void => removeEventListener();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      refsState,
      ignoreClass,
      excludeScrollbar,
      disabled,
      detectIFrame,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(eventTypes),
    ]
  );

  return ref;
};

export default useOnclickOutside;
